import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { QueryObserverResult } from 'react-query';

import Button from '../../components/Button';
import Breakpoints from '../../design-utils/breakpoints';
import Spinner from '../../components/Spinner';
import type { ProductsDataResponse } from '../api';

import ProductCard, {
  CardImage,
  CardSubSubTitle,
} from '../../components/ProductCard/ProductCard';
import { showMinMaxPrice } from '../utils';
import useInifiniteScroll from '../../utils/useInifiniteScroll';
import { Paragraph } from '../../components/Typography';

const CardsContainer = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 1010px;
  display: grid;
  flex: 1 1 0;
  grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
  overflow-y: scroll;
  position: relative;

  @media screen and (max-width: ${Breakpoints.md}) {
    justify-items: center;
  }
`;

const SingleCardContainer = styled.div.attrs<{ idClass: string | number }>(
  ({ idClass }) => ({
    className: `single-card-container ${idClass}`,
  }),
)<{ idClass: string | number }>`
  display: flex;
  width: 100%;
  padding: 8px;
  justify-content: center;
`;

const Feedback = styled.div`
  font-weight: bold;
  text-align: center;
  align-self: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%);
`;

const FeedbackAction = styled.div`
  margin-top: 12px;
`;

interface SpinnerProps {
  isActive: boolean;
}
const SpinnerContainer = styled.div<SpinnerProps>`
  display: flex;
  justify-content: center;
  grid-column: 1 / -1;
  align-self: flex-end;
  overflow: hidden;
  padding: 30px 0;
  visibility: ${(props) => (props.isActive ? 'visible' : 'hidden')};
`;

const LazyControl = styled.div`
  height: 1px;
  opacity: 0;
`;

export interface SelectProductProps {
  isFetching: boolean;
  hasErrors?: boolean;
  storeProducts: GlobalProduct[] | null;
  handleChoose: (product: GlobalProduct['id'], isBundle?: boolean) => void;
  loadMoreProducts?: () => void;
  refetch?: () => Promise<QueryObserverResult<ProductsDataResponse>>;
  showDiscountedPrice?: boolean;
}

const Products = ({
  storeProducts,
  handleChoose,
  isEmpty,
  hasErrors,
  onRetry,
  isFteching,
  showDiscountedPrice,
}) => {
  const { t } = useTranslation();

  if (hasErrors) {
    return (
      <Feedback data-testid="products-haserrors">
        {t('product_swap.fetch_error')}
        <FeedbackAction>
          <Button disabled={isFteching} outlined onClick={onRetry}>
            {t('product_swap.retry')}
          </Button>
        </FeedbackAction>
      </Feedback>
    );
  }

  if (isEmpty) {
    return (
      <Feedback data-testid="products-feedback">
        {t('product_swap.empty')}
      </Feedback>
    );
  }

  return storeProducts?.map((product: GlobalProduct) => (
    <SingleCardContainer
      key={product.id}
      idClass={product.id}
      data-testid={product.id}
    >
      <ProductCard id={`${product.id}`}>
        <CardImage image={product.image} alt={product.title} />
        <CardSubSubTitle
          className="bundle-product-card__subtitle"
          modifiers={['body']}
        >
          {product.title}
        </CardSubSubTitle>
        <Button
          fullWidth
          filled
          onClick={() => handleChoose(product.id, product.isBundle)}
        >
          {product.isBundle
            ? t('product_swap.customize_bundle')
            : t('product_swap.choose')}
        </Button>

        <div style={{ marginTop: 20 }}>
          {showDiscountedPrice &&
          +product.priceMin > +product.discountedPriceMin &&
          +product.priceMax > +product.discountedPriceMax ? (
            <>
              <Paragraph
                modifiers={['body', 'subdued']}
                style={{ textDecoration: 'line-through' }}
              >
                {showMinMaxPrice({
                  min: product.priceMin,
                  max: product.priceMax,
                  currency: product.priceCurrency as string,
                })}
              </Paragraph>
              <Paragraph modifiers={['body', 'subdued']}>
                {showMinMaxPrice({
                  min: product.discountedPriceMin?.toString(),
                  max: product.discountedPriceMax?.toString(),
                  currency: product.priceCurrency as string,
                })}
              </Paragraph>
            </>
          ) : (
            showMinMaxPrice({
              min: product.priceMin,
              max: product.priceMax,
              currency: product.priceCurrency as string,
            })
          )}
        </div>
      </ProductCard>
    </SingleCardContainer>
  ));
};

const SelectProduct: React.FC<SelectProductProps> = ({
  isFetching,
  storeProducts,
  handleChoose,
  loadMoreProducts,
  hasErrors,
  refetch,
  showDiscountedPrice,
}) => {
  const callback = useCallback(() => {
    if (!isFetching && loadMoreProducts) {
      loadMoreProducts();
    }
  }, [loadMoreProducts, isFetching]);
  const lastItemRef = useInifiniteScroll(callback, !isFetching);
  const isEmptyCollection =
    !isFetching && storeProducts && storeProducts.length === 0;

  return (
    <>
      <CardsContainer>
        <Products
          hasErrors={hasErrors}
          isEmpty={isEmptyCollection}
          storeProducts={storeProducts}
          handleChoose={handleChoose}
          onRetry={refetch}
          isFteching={isFetching}
          showDiscountedPrice={showDiscountedPrice}
        />
        {!isEmptyCollection ? (
          <>
            <LazyControl ref={lastItemRef}>Control</LazyControl>
            <SpinnerContainer
              className="bundle-loading-container"
              isActive={isFetching}
            >
              <Spinner>Loading</Spinner>
            </SpinnerContainer>
          </>
        ) : null}
      </CardsContainer>
    </>
  );
};

export default SelectProduct;
